import { useCallback } from "react";
import { type To } from "react-router";

import { appRoutes } from "@/app/router/appRoutes";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { windowModule } from "@/modules/window";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { useAppStore } from "@/store";
import { type TabOpeningStrategy } from "@/store/navigation/NavigationTabManager";
import { SettingsTabId } from "@/store/pages/SettingsPageStore";
import type {
  AppCalloutBannerActionSlot,
  AppCalloutBannerEventParams,
} from "@/domains/metrics/tracked-event";

interface AppBannerLinkOpenResult {
  didOpen: boolean;
  openedWindow: Window | null;
}

const MEM_AI_HOSTNAME = "mem.ai";
const NEW_APP_TAB_STRATEGY = "NEW_TAB" as TabOpeningStrategy;

const isMemAiHostname = (hostname: string) =>
  hostname === MEM_AI_HOSTNAME || hostname.endsWith(`.${MEM_AI_HOSTNAME}`);

const SETTINGS_TAB_BY_MODAL_ROUTE_PATH = new Map<string, SettingsTabId>([
  [appRoutes.settings({}).path, SettingsTabId.NotificationPreferencesPage],
  [appRoutes.settingsNotificationPreferences({}).path, SettingsTabId.NotificationPreferencesPage],
  [appRoutes.settingsEmailPreferences({}).path, SettingsTabId.NotificationPreferencesPage],
  [appRoutes.settingsVoiceMode({}).path, SettingsTabId.VoiceModePage],
  [appRoutes.settingsChat({}).path, SettingsTabId.NotificationPreferencesPage],
  [appRoutes.settingsMemAgent({}).path, SettingsTabId.MemAgentPage],
  [appRoutes.settingsSubscriptionManagement({}).path, SettingsTabId.SubscriptionManagementPage],
  [appRoutes.settingsReferralProgram({}).path, SettingsTabId.ReferralProgramPage],
  [appRoutes.settingsAccountManagement({}).path, SettingsTabId.ProfilePage],
  [appRoutes.settingsExports({}).path, SettingsTabId.ExportsPage],
  [appRoutes.settingsDebug({}).path, SettingsTabId.DebugAccountPage],
  [appRoutes.settingsDebugAccount({}).path, SettingsTabId.DebugAccountPage],
  [appRoutes.settingsDebugSandbox({}).path, SettingsTabId.DebugSandboxPage],
  [appRoutes.integrations({}).path, SettingsTabId.CalendarAndMeetingsPage],
  [appRoutes.integrationsSlack({}).path, SettingsTabId.MemAgentCommunicationChannelsPage],
  [appRoutes.integrationsZapier({}).path, SettingsTabId.ZapierPage],
  [appRoutes.integrationsApi({}).path, SettingsTabId.ApiKeysPage],
  [appRoutes.integrationsEmail({}).path, SettingsTabId.EmailToMemPage],
  [appRoutes.integrationsChromeExtension({}).path, SettingsTabId.ChromeExtensionPage],
  [appRoutes.integrationsMcp({}).path, SettingsTabId.McpPage],
  [appRoutes.integrationsImports({}).path, SettingsTabId.ImportsPage],
  [appRoutes.templatesList({}).path, SettingsTabId.TemplatesPage],
]);

const normalizePathname = (pathname: string) =>
  pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;

export const isMemAgentSettingsMemAiUrl = (url: string | null): boolean => {
  if (!url) {
    return false;
  }

  try {
    const parsedUrl = new URL(url);

    return (
      isMemAiHostname(parsedUrl.hostname) &&
      normalizePathname(parsedUrl.pathname) === appRoutes.settingsMemAgent({}).path
    );
  } catch {
    return false;
  }
};

const getMemAiRoute = (url: URL): To => ({
  pathname: url.pathname || "/",
  search: url.search,
  hash: url.hash,
});

export const useAppBannerLinkOnClickHandler = ({
  url,
  actionSlot,
  trackingContext,
}: {
  url: string;
  actionSlot?: AppCalloutBannerActionSlot;
  trackingContext?: AppCalloutBannerEventParams;
}) => {
  const { store } = useAppStore();

  const onClickHandler = useCallback(async (): Promise<AppBannerLinkOpenResult> => {
    const normalizedUrl = url;

    try {
      const parsedUrl = new URL(normalizedUrl);

      if (trackingContext && actionSlot) {
        trackEvent(TrackedEvent.AppCalloutBannerActionClicked, {
          ...trackingContext,
          action_slot: actionSlot,
          url: parsedUrl.toString(),
        });
      }

      if (isMemAiHostname(parsedUrl.hostname)) {
        if (
          isMemAgentSettingsMemAiUrl(normalizedUrl) &&
          !store.subscriptions.hasProactiveAccess
        ) {
          store.subscriptions.openProactiveUpsell();

          return {
            didOpen: true,
            openedWindow: null,
          };
        }

        const settingsTabId = SETTINGS_TAB_BY_MODAL_ROUTE_PATH.get(
          normalizePathname(parsedUrl.pathname)
        );

        if (settingsTabId) {
          store.settingsModal.open(settingsTabId);
        } else {
          store.navigation.tabs.openTab({
            path: getMemAiRoute(parsedUrl),
            strategy: NEW_APP_TAB_STRATEGY,
          });
        }

        return {
          didOpen: true,
          openedWindow: null,
        };
      }

      const openedWindow = await windowModule.openInNewTab({ url: parsedUrl });

      return {
        didOpen: true,
        openedWindow,
      };
    } catch (unknownErr) {
      const err = unknownErr as Error;

      logger.error({
        message: `[useAppBannerLinkOnClickHandler] Failed to open URL.`,
        info: {
          error: objectModule.safeErrorAsJson(err),
          url,
        },
      });

      return {
        didOpen: false,
        openedWindow: null,
      };
    }
  }, [actionSlot, store.navigation.tabs, store.settingsModal, store.subscriptions, trackingContext, url]);

  return { onClickHandler };
};
